import React, { useState, useEffect, useRef } from 'react';
import { devError } from '../../lib/devLog';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  FileText, Edit, Eye, Save, X, Plus, Trash2, 
  Image as ImageIcon, Type, Layout, ChevronDown, ChevronUp,
  Settings, Globe, Search, Copy, Palette, Upload, Loader, Wand2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import MediaPickerModal from './MediaPickerModal';
import ArtworkPickerModal from './ArtworkPickerModal';
import CompositeRoomModal from './CompositeRoomModal';
import { adminFetch, withAdminAuth } from '../../lib/adminAuth';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// All pages in the website
const WEBSITE_PAGES = [
  { id: 'home', name: 'Home', path: '/', editable: true },
  { id: 'about', name: 'About Us', path: '/about', editable: true },
  { id: 'shop', name: 'Shop', path: '/shop', editable: true },
  { id: 'artists', name: 'Artists', path: '/artists', editable: true },
  { id: 'contact', name: 'Contact', path: '/contact', editable: true },
  { id: 'exhibitions', name: 'Exhibitions', path: '/exhibitions', editable: true },
  { id: 'news', name: 'News', path: '/news', editable: true },
  { id: 'picture-framing', name: 'Picture Framing', path: '/picture-framing', editable: true },
  { id: 'art-consultation', name: 'Art Consultation', path: '/art-consultation', editable: true },
  { id: 'banksy', name: 'Banksy Gallery', path: '/banksy', editable: true },
  { id: 'banksy-market', name: 'Banksy Market Tracker', path: '/banksy/market', editable: true },
];

/**
 * HeroCarouselEditor — dedicated inline editor for the homepage
 * cinematic carousel. Renders one row per scene with image URL, artist,
 * title, and href fields, plus add/remove/reorder controls.
 */
const HeroCarouselEditor = ({ scenes, onChange, onPickImage }) => {
  const [artworkPickerIdx, setArtworkPickerIdx] = useState(null);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [compositeIdx, setCompositeIdx] = useState(null);
  const fileInputRef = useRef(null);
  const fileTargetIdxRef = useRef(null);
  const updateScene = (index, field, value) => {
    const next = scenes.map((s, i) => (i === index ? { ...s, [field]: value } : s));
    onChange(next);
  };
  // Fill an entire scene from a picked artwork so admin gets a one-click swap.
  // Persists `sourceArtwork` = the original artwork image URL (separate from
  // `src` which holds the composite/display image) so the Regenerate-room
  // button can re-composite the piece into a new room without a fresh pick.
  const applyArtwork = (index, artwork) => {
    const src = artwork.imageUrl?.startsWith('http')
      ? artwork.imageUrl
      : `${API_URL}${artwork.imageUrl || ''}`;
    const next = scenes.map((s, i) => (i === index ? {
      ...s,
      kind: 'artwork',
      src,
      sourceArtwork: src,
      artist: artwork.artistName || s.artist || '',
      title: artwork.title || s.title || '',
      href: artwork.slug ? `/artwork/${artwork.slug}` : (s.href || '/shop'),
    } : s));
    onChange(next);
  };
  // Called when the composite modal returns a fresh /api/cloud-media URL —
  // updates the scene's `src` (what displays) but leaves `sourceArtwork`
  // untouched so the same piece can be re-composited again later.
  const applyComposite = (index, newSrc, roomPrompt) => {
    const next = scenes.map((s, i) => (i === index ? {
      ...s, src: newSrc, roomPrompt,
    } : s));
    onChange(next);
  };
  const addScene = (kind = 'artwork') => {
    if (kind === 'room') {
      onChange([...scenes, { kind: 'room', src: '', caption: '', subcaption: '', href: '/consultations' }]);
    } else {
      onChange([...scenes, { kind: 'artwork', src: '', artist: '', title: '', href: '' }]);
    }
  };
  const removeScene = (index) => {
    if (!window.confirm('Remove this scene?')) return;
    onChange(scenes.filter((_, i) => i !== index));
  };
  const moveScene = (index, dir) => {
    const next = [...scenes];
    const j = dir === 'up' ? index - 1 : index + 1;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    onChange(next);
  };

  // Direct device upload — writes to Emergent Object Storage, then sets the
  // scene's `src` to the returned CDN path so the hero renders it immediately.
  const startUpload = (index) => {
    fileTargetIdxRef.current = index;
    fileInputRef.current?.click();
  };
  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    const idx = fileTargetIdxRef.current;
    e.target.value = '';
    if (!file || idx === null) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please pick an image file');
      return;
    }
    setUploadingIdx(idx);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder', 'hero');
      const res = await adminFetch(`${API_URL}/api/admin/upload`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      const url = data.url || data.path;
      if (!url) throw new Error('Upload response missing url');
      const absUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
      updateScene(idx, 'src', absUrl);
      toast.success('Photo uploaded — remember to click Save Changes');
    } catch (err) {
      toast.error(`Upload failed: ${err.message}`);
    } finally {
      setUploadingIdx(null);
      fileTargetIdxRef.current = null;
    }
  };

  return (
    <div className="space-y-4" data-testid="hero-carousel-editor">
      <p className="text-xs text-gray-500">
        Each scene rotates on the homepage hero. Image path is served from the frontend&rsquo;s
        <code className="mx-1 px-1 bg-gray-100">/hero/</code>or
        <code className="mx-1 px-1 bg-gray-100">/hero-rooms/</code>directory (or paste any full URL).
      </p>
      {scenes.map((scene, i) => {
        const kind = scene.kind || 'artwork';
        const isRoom = kind === 'room';
        return (
          <div
            key={i}
            className={`border rounded-lg p-3 space-y-2 ${isRoom ? 'border-amber-200 bg-amber-50/40' : 'border-gray-200'}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-600">Scene {i + 1}</span>
                {/* Kind selector — flips the visible fields between the
                    Featured-work layout and the "In residence" room layout. */}
                <select
                  value={kind}
                  onChange={(e) => {
                    const nextKind = e.target.value;
                    // Reset copy fields to sensible defaults when the kind flips so
                    // the DB row doesn't carry stale text from the other layout.
                    if (nextKind === 'room') {
                      updateScene(i, 'kind', 'room');
                      updateScene(i, 'href', scene.href || '/consultations');
                    } else {
                      updateScene(i, 'kind', 'artwork');
                    }
                  }}
                  className="text-xs px-2 py-1 border border-gray-300 rounded bg-white"
                  data-testid={`hero-scene-kind-${i}`}
                >
                  <option value="artwork">Featured artwork</option>
                  <option value="room">Residence room</option>
                </select>
              </div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => moveScene(i, 'up')} disabled={i === 0}
                  className="p-1 hover:bg-gray-100 rounded disabled:opacity-30" title="Move up"
                  data-testid={`hero-scene-up-${i}`}>
                  <ChevronUp className="w-3.5 h-3.5" />
                </button>
                <button type="button" onClick={() => moveScene(i, 'down')} disabled={i === scenes.length - 1}
                  className="p-1 hover:bg-gray-100 rounded disabled:opacity-30" title="Move down"
                  data-testid={`hero-scene-down-${i}`}>
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                <button type="button" onClick={() => removeScene(i)}
                  className="p-1 hover:bg-red-50 text-red-600 rounded" title="Remove"
                  data-testid={`hero-scene-remove-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div className="col-span-1 md:col-span-2 flex gap-2">
                <input type="text" placeholder="Image src (/hero/... or https://…)"
                  value={scene.src || ''}
                  onChange={(e) => updateScene(i, 'src', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
                  data-testid={`hero-scene-src-${i}`}
                />
                {onPickImage && (
                  <button
                    type="button"
                    onClick={() => onPickImage((url) => updateScene(i, 'src', url))}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    title="Choose from media library"
                    data-testid={`hero-scene-picker-${i}`}
                  >
                    <ImageIcon className="w-4 h-4" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => startUpload(i)}
                  disabled={uploadingIdx === i}
                  className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  title="Upload a photo from this device"
                  data-testid={`hero-scene-upload-${i}`}
                >
                  {uploadingIdx === i
                    ? <Loader className="w-4 h-4 animate-spin" />
                    : <Upload className="w-4 h-4" />}
                </button>
                {!isRoom && (
                  <button
                    type="button"
                    onClick={() => setArtworkPickerIdx(i)}
                    className="px-3 py-2 border border-black bg-black text-white rounded-lg hover:bg-gray-800 flex items-center gap-1.5 text-xs whitespace-nowrap"
                    title="Pick an artwork from the catalog (auto-fills artist, title & link)"
                    data-testid={`hero-scene-artwork-picker-${i}`}
                  >
                    <Palette className="w-4 h-4" />
                    Pick artwork
                  </button>
                )}
                {!isRoom && (
                  <button
                    type="button"
                    onClick={() => setCompositeIdx(i)}
                    disabled={!(scene.sourceArtwork || scene.src)}
                    className="px-3 py-2 border border-purple-700 bg-purple-700 text-white rounded-lg hover:bg-purple-800 flex items-center gap-1.5 text-xs whitespace-nowrap disabled:opacity-40"
                    title="Regenerate an in-situ room composite with a new window view via Nano Banana"
                    data-testid={`hero-scene-composite-${i}`}
                  >
                    <Wand2 className="w-4 h-4" />
                    Regenerate room
                  </button>
                )}
              </div>
              {isRoom ? (
                <>
                  <input type="text" placeholder='Caption (e.g. "Placement in situ")'
                    value={scene.caption || ''}
                    onChange={(e) => updateScene(i, 'caption', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
                    data-testid={`hero-scene-caption-${i}`}
                  />
                  <input type="text" placeholder='Subcaption (e.g. "Hampstead drawing room · Warhol above the fireplace")'
                    value={scene.subcaption || ''}
                    onChange={(e) => updateScene(i, 'subcaption', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
                    data-testid={`hero-scene-subcaption-${i}`}
                  />
                </>
              ) : (
                <>
                  <input type="text" placeholder="Artist"
                    value={scene.artist || ''}
                    onChange={(e) => updateScene(i, 'artist', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
                    data-testid={`hero-scene-artist-${i}`}
                  />
                  <input type="text" placeholder="Title"
                    value={scene.title || ''}
                    onChange={(e) => updateScene(i, 'title', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
                    data-testid={`hero-scene-title-${i}`}
                  />
                </>
              )}
              <input type="text" placeholder={isRoom ? 'Link (e.g. /consultations)' : 'Link (e.g. /shop?artist=Name)'}
                value={scene.href || ''}
                onChange={(e) => updateScene(i, 'href', e.target.value)}
                className="col-span-1 md:col-span-2 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
                data-testid={`hero-scene-href-${i}`}
              />
            </div>
          </div>
        );
      })}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => addScene('artwork')}
          className="flex-1 py-2 border border-dashed border-gray-300 rounded-lg text-sm hover:border-black hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
          data-testid="hero-scene-add"
        >
          <Plus className="w-4 h-4" />
          Add featured artwork
        </button>
        <button
          type="button"
          onClick={() => addScene('room')}
          className="flex-1 py-2 border border-dashed border-amber-300 rounded-lg text-sm hover:border-amber-500 hover:bg-amber-50 transition-colors flex items-center justify-center gap-2"
          data-testid="hero-scene-add-room"
        >
          <Plus className="w-4 h-4" />
          Add residence room
        </button>
      </div>

      <ArtworkPickerModal
        open={artworkPickerIdx !== null}
        onClose={() => setArtworkPickerIdx(null)}
        onPick={(artwork) => {
          if (artworkPickerIdx !== null) applyArtwork(artworkPickerIdx, artwork);
        }}
        existingIds={[]}
        maxSelect={1}
      />
      <CompositeRoomModal
        open={compositeIdx !== null}
        onClose={() => setCompositeIdx(null)}
        artworkSrc={compositeIdx !== null ? (scenes[compositeIdx]?.sourceArtwork || scenes[compositeIdx]?.src) : null}
        initialPrompt={compositeIdx !== null ? scenes[compositeIdx]?.roomPrompt : ''}
        onGenerated={(newUrl, roomPrompt) => {
          if (compositeIdx !== null) applyComposite(compositeIdx, newUrl, roomPrompt);
        }}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={onFileSelected}
        className="hidden"
        data-testid="hero-scene-file-input"
      />
    </div>
  );
};


const UniversalPageManager = () => {
  const { token } = useAuth();
  const [pages, setPages] = useState([]);
  const [artists, setArtists] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [pageContent, setPageContent] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddSection, setShowAddSection] = useState(false);
  const [previewKey, setPreviewKey] = useState(0); // force iframe refresh after save
  const [showPreview, setShowPreview] = useState(true);
  // Media picker state: `{ onSelect: fn }` when open, null when closed.
  // This lets any block editor call `setPicker({ onSelect })` to open the
  // shared modal without prop-drilling.
  const [picker, setPicker] = useState(null);

  useEffect(() => {
    fetchAllPages();
    fetchArtists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllPages = async () => {
    try {
      // Fetch existing page data from backend
      const response = await axios.get(`${API_URL}/api/admin/pages`, withAdminAuth());
      setPages(response.data || []);
    } catch (error) {
      devError('Error fetching pages:', error);
      // Initialize with default structure if no pages exist
      setPages(WEBSITE_PAGES.map(page => ({
        ...page,
        sections: [],
        seo: {
          title: page.name,
          description: '',
          keywords: ''
        },
        published: true
      })));
    }
  };

  const fetchArtists = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/artists?include_hidden=1`);
      // Map artists into page-manager-compatible sidebar entries.
      // Slug pattern `artist-{artist-slug}` keeps it URL-safe for the
      // `/api/pages/{slug}` route (which doesn't accept slashes).
      const asPages = (res.data || [])
        .filter((a) => a?.slug)
        .map((a) => ({
          id: `artist-${a.slug}`,
          slug: `artist-${a.slug}`,
          name: a.name,
          path: `/artist/${a.slug}`,
          kind: 'artist',
          editable: true,
        }))
        .sort((x, y) => x.name.localeCompare(y.name));
      setArtists(asPages);
    } catch (error) {
      devError('Error fetching artists:', error);
    }
  };

  const handleSelectPage = async (page) => {
    setSelectedPage(page);
    setEditingSection(null);
    setPreviewKey((k) => k + 1);
    
    // Fetch detailed page content
    const slug = page.slug || page.id;
    try {
      const response = await axios.get(`${API_URL}/api/admin/pages/${slug}`, withAdminAuth());
      setPageContent(response.data);
    } catch (error) {
      devError('Error fetching page content:', error);
      // Initialize empty content structure
      setPageContent({
        id: page.id,
        slug: page.slug || page.id,
        name: page.name || page.title,
        path: page.path,
        sections: [],
        seo: {
          title: page.title || page.name,
          description: '',
          keywords: ''
        },
        published: true
      });
    }
  };

  const handleSavePage = async () => {
    try {
      // Backend expects slug as the path param, not Mongo `id`
      const slug = pageContent.slug || pageContent.id || selectedPage?.id;
      await axios.put(
        `${API_URL}/api/admin/pages/${slug}`,
        pageContent,
        withAdminAuth()
      );
      alert('Page saved successfully!');
      fetchAllPages();
      setPreviewKey((k) => k + 1); // refresh live preview iframe
    } catch (error) {
      devError('Error saving page:', error);
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : error.message;
      alert(`Error saving page: ${msg}`);
    }
  };

  const handleAddSection = (sectionType) => {
    const newSection = {
      id: Date.now().toString(),
      type: sectionType,
      content: getSectionDefaultContent(sectionType),
      visible: true,
      order: (pageContent.sections?.length || 0)
    };

    setPageContent({
      ...pageContent,
      sections: [...(pageContent.sections || []), newSection]
    });
    setShowAddSection(false);
  };

  const getSectionDefaultContent = (type) => {
    switch (type) {
      case 'hero':
        return {
          title: 'Hero Title',
          subtitle: 'Subtitle text',
          backgroundImage: '',
          ctaText: 'Learn More',
          ctaLink: '#'
        };
      case 'hero_carousel':
        // Cinematic scenes on the homepage.
        // Each scene: { kind: 'artwork'|'room', src, artist?, title?, caption?, subcaption?, href }
        return {
          scenes: [
            { kind: 'artwork', src: '/hero/05_banksy_flying_copper.jpg', artist: 'Banksy', title: 'Flying Copper', href: '/shop?artist=Banksy' },
            { kind: 'room', src: '/hero-rooms/01_drawing_room.jpg', caption: 'Placement in situ', subcaption: 'Hampstead drawing room · Warhol above the fireplace', href: '/consultations' },
            { kind: 'artwork', src: '/hero/03_warhol_soup.jpg', artist: 'Andy Warhol', title: "Campbell's Soup II", href: '/shop?artist=Andy%20Warhol' },
            { kind: 'room', src: '/hero-rooms/07_library.jpg', caption: 'From gallery to library', subcaption: 'Kensington residence · Michael Joseph, Terry O\u2019Neill', href: '/consultations' },
            { kind: 'artwork', src: '/hero/02_michael_joseph_beggars.jpg', artist: 'Michael Joseph', title: 'Sarum Chase · The Beggars, 1968', href: '/artist/michael-joseph' },
            { kind: 'room', src: '/hero-rooms/04_master_bedroom.jpg', caption: 'Lived-in curation', subcaption: 'Notting Hill master bedroom', href: '/consultations' },
            { kind: 'artwork', src: '/hero/04_hirst_spot.jpg', artist: 'Damien Hirst', title: 'Spot Mickey', href: '/shop?artist=Damien%20Hirst' },
            { kind: 'room', src: '/hero-rooms/06_living_room.jpg', caption: 'A collection you live with', subcaption: 'Chelsea drawing room · Hirst, Parkinson, Bown', href: '/consultations' },
            { kind: 'artwork', src: '/hero/01_jordi_aurora.jpg', artist: 'Jordi Diaz Alam\u00e0', title: 'Vulcano', href: '/shop?artist=Jordi%20Diaz%20Alama' },
          ],
        };
      case 'text':
        return {
          heading: 'Section Heading',
          body: 'Your text content here...',
          alignment: 'left'
        };
      case 'image':
        return {
          imageUrl: '',
          caption: '',
          alt: ''
        };
      case 'gallery':
        return {
          title: 'Gallery',
          images: []
        };
      case 'cta':
        return {
          title: 'Call to Action',
          description: 'Description text',
          buttonText: 'Click Here',
          buttonLink: '#'
        };
      default:
        return {};
    }
  };

  const handleUpdateSection = (sectionId, field, value) => {
    setPageContent({
      ...pageContent,
      sections: pageContent.sections.map(section =>
        section.id === sectionId
          ? { ...section, content: { ...section.content, [field]: value } }
          : section
      )
    });
  };

  const handleDeleteSection = (sectionId) => {
    if (!window.confirm('Delete this section?')) return;
    
    setPageContent({
      ...pageContent,
      sections: pageContent.sections.filter(s => s.id !== sectionId)
    });
  };

  const handleDuplicateSection = (sectionId) => {
    const source = pageContent.sections?.find((s) => s.id === sectionId);
    if (!source) return;
    // Deep-clone via JSON so nested arrays (gallery images, hero_carousel
    // scenes) are copies rather than shared references.
    const clone = JSON.parse(JSON.stringify(source));
    clone.id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const idx = pageContent.sections.findIndex((s) => s.id === sectionId);
    const next = [...pageContent.sections];
    next.splice(idx + 1, 0, clone);
    // Re-number `order` to keep the array positions and the stored order in sync.
    setPageContent({
      ...pageContent,
      sections: next.map((s, i) => ({ ...s, order: i })),
    });
    // Immediately open the newly-cloned block so the admin can tweak it.
    setEditingSection(clone.id);
  };

  const handleMoveSection = (sectionId, direction) => {
    const sections = [...pageContent.sections];
    const index = sections.findIndex(s => s.id === sectionId);
    
    if (direction === 'up' && index > 0) {
      [sections[index], sections[index - 1]] = [sections[index - 1], sections[index]];
    } else if (direction === 'down' && index < sections.length - 1) {
      [sections[index], sections[index + 1]] = [sections[index + 1], sections[index]];
    }
    
    setPageContent({ ...pageContent, sections });
  };

  const filteredPages = WEBSITE_PAGES.filter(page =>
    page.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredArtists = artists.filter((a) =>
    a.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-light mb-2">Page Manager</h1>
        <p className="text-gray-600">Edit all pages on your website</p>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Sidebar - Pages List */}
        <div className="col-span-12 md:col-span-3 bg-white rounded-lg shadow-sm border border-gray-200 self-start">
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search pages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-sm"
              />
            </div>
          </div>

          <div className="divide-y divide-gray-100 max-h-[80vh] overflow-y-auto">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
              <span className="text-[10px] uppercase tracking-widest text-gray-500 font-medium">
                Site pages · {filteredPages.length}
              </span>
            </div>
            {filteredPages.map((page) => (
              <button
                key={page.id}
                onClick={() => handleSelectPage(page)}
                className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center justify-between ${
                  selectedPage?.id === page.id ? 'bg-gray-100' : ''
                }`}
                data-testid={`page-list-item-${page.slug || page.id}`}
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-gray-400" />
                  <div>
                    <div className="font-medium text-sm">{page.name || page.title}</div>
                    <div className="text-xs text-gray-500">{page.path}</div>
                  </div>
                </div>
                {selectedPage?.id === page.id && (
                  <Edit className="w-4 h-4 text-black" />
                )}
              </button>
            ))}

            {filteredArtists.length > 0 && (
              <div className="px-4 py-2 bg-gray-50 border-b border-t border-gray-100">
                <span className="text-[10px] uppercase tracking-widest text-gray-500 font-medium">
                  Artist pages · {filteredArtists.length}
                </span>
              </div>
            )}
            {filteredArtists.map((page) => (
              <button
                key={page.id}
                onClick={() => handleSelectPage(page)}
                className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center justify-between ${
                  selectedPage?.id === page.id ? 'bg-gray-100' : ''
                }`}
                data-testid={`page-list-item-${page.slug}`}
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-gray-400" />
                  <div>
                    <div className="font-medium text-sm">{page.name}</div>
                    <div className="text-xs text-gray-500">{page.path}</div>
                  </div>
                </div>
                {selectedPage?.id === page.id && (
                  <Edit className="w-4 h-4 text-black" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Main Editor Area */}
        <div className={`col-span-12 ${showPreview ? 'md:col-span-4' : 'md:col-span-9'}`}>
          {!selectedPage ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <Globe className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-xl font-medium mb-2">Select a Page to Edit</h3>
              <p className="text-gray-600">Choose a page from the list to start editing</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Page Header */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-light mb-1">{pageContent.name || selectedPage.name}</h2>
                    <p className="text-sm text-gray-600">{pageContent.path || selectedPage.path}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setShowPreview((v) => !v)}
                      className="hidden md:flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                      data-testid="page-preview-toggle"
                      title={showPreview ? 'Hide live preview' : 'Show live preview'}
                    >
                      <Eye className="w-4 h-4" />
                      {showPreview ? 'Hide Preview' : 'Show Preview'}
                    </button>
                    <a
                      href={selectedPage.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                    >
                      <Eye className="w-4 h-4" />
                      Open
                    </a>
                    <button
                      onClick={handleSavePage}
                      className="flex items-center gap-2 px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm"
                      data-testid="page-save-btn"
                    >
                      <Save className="w-4 h-4" />
                      Save Changes
                    </button>
                  </div>
                </div>

                {/* SEO Settings */}
                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={() => setEditingSection('seo')}
                    className="flex items-center gap-2 text-sm font-medium mb-3"
                  >
                    <Settings className="w-4 h-4" />
                    SEO Settings
                  </button>

                  {editingSection === 'seo' && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium mb-1">Page Title</label>
                        <input
                          type="text"
                          value={pageContent.seo?.title || ''}
                          onChange={(e) => setPageContent({
                            ...pageContent,
                            seo: { ...pageContent.seo, title: e.target.value }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Meta Description</label>
                        <textarea
                          rows={2}
                          value={pageContent.seo?.description || ''}
                          onChange={(e) => setPageContent({
                            ...pageContent,
                            seo: { ...pageContent.seo, description: e.target.value }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Keywords (comma-separated)</label>
                        <input
                          type="text"
                          value={pageContent.seo?.keywords || ''}
                          onChange={(e) => setPageContent({
                            ...pageContent,
                            seo: { ...pageContent.seo, keywords: e.target.value }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Page Sections */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">Page Sections</h3>
                  <button
                    onClick={() => setShowAddSection(!showAddSection)}
                    className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Add Section
                  </button>
                </div>

                {/* Add Section Menu */}
                {showAddSection && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <p className="text-sm font-medium mb-3">Choose section type:</p>
                    <div className="grid grid-cols-3 gap-2 md:grid-cols-6">
                      {['hero', 'hero_carousel', 'text', 'image', 'gallery', 'cta'].map((type) => (
                        <button
                          key={type}
                          onClick={() => handleAddSection(type)}
                          className="p-3 border border-gray-200 rounded-lg hover:border-black transition-colors text-center"
                        >
                          <Layout className="w-5 h-5 mx-auto mb-1 text-gray-600" />
                          <span className="text-xs capitalize">{type.replace('_', ' ')}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sections List */}
                {pageContent.sections && pageContent.sections.length > 0 ? (
                  <div className="space-y-3">
                    {pageContent.sections.map((section, index) => (
                      <div
                        key={section.id}
                        className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Layout className="w-4 h-4 text-gray-400" />
                            <span className="font-medium text-sm capitalize">{section.type} Section</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleMoveSection(section.id, 'up')}
                              disabled={index === 0}
                              className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                            >
                              <ChevronUp className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleMoveSection(section.id, 'down')}
                              disabled={index === pageContent.sections.length - 1}
                              className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                            >
                              <ChevronDown className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setEditingSection(editingSection === section.id ? null : section.id)}
                              className="p-1 hover:bg-gray-100 rounded"
                              title={editingSection === section.id ? 'Close editor' : 'Edit'}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDuplicateSection(section.id)}
                              className="p-1 hover:bg-gray-100 rounded"
                              title="Duplicate this section"
                              data-testid={`section-duplicate-${section.id}`}
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteSection(section.id)}
                              className="p-1 hover:bg-red-50 text-red-600 rounded"
                              title="Delete section"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Section Editor */}
                        {editingSection === section.id && (
                          <div className="pt-3 border-t border-gray-200 space-y-3">
                            {section.type === 'hero_carousel' ? (
                              <HeroCarouselEditor
                                scenes={section.content.scenes || []}
                                onChange={(scenes) => handleUpdateSection(section.id, 'scenes', scenes)}
                                onPickImage={(cb) => setPicker({ onSelect: cb })}
                              />
                            ) : (
                              Object.entries(section.content).map(([key, value]) => (
                              <div key={key}>
                                <label className="block text-sm font-medium mb-1 capitalize">
                                  {key.replace(/([A-Z])/g, ' $1').trim()}
                                </label>
                                {key.includes('Image') || key.includes('image') ? (
                                  <div className="flex gap-2">
                                    <input
                                      type="text"
                                      value={value}
                                      onChange={(e) => handleUpdateSection(section.id, key, e.target.value)}
                                      placeholder="Image URL"
                                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-sm"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => setPicker({
                                        onSelect: (url) => handleUpdateSection(section.id, key, url),
                                      })}
                                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                      title="Choose from media library"
                                      data-testid={`section-image-picker-${section.id}-${key}`}
                                    >
                                      <ImageIcon className="w-4 h-4" />
                                    </button>
                                  </div>
                                ) : typeof value === 'string' && value.length > 100 ? (
                                  <textarea
                                    rows={3}
                                    value={value}
                                    onChange={(e) => handleUpdateSection(section.id, key, e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-sm"
                                  />
                                ) : (
                                  <input
                                    type="text"
                                    value={value}
                                    onChange={(e) => handleUpdateSection(section.id, key, e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-sm"
                                  />
                                )}
                              </div>
                            ))
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                    <Layout className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-gray-600">No sections yet. Click &ldquo;Add Section&rdquo; to start building this page.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Live Preview Pane */}
        {selectedPage && showPreview && (
          <div className="hidden md:flex col-span-5 bg-white rounded-lg shadow-sm border border-gray-200 flex-col self-start sticky top-4">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-gray-700">
                <Eye className="w-4 h-4" />
                <span className="font-medium">Live preview</span>
                <span className="text-gray-400">·</span>
                <span className="text-gray-500 font-mono text-xs">{selectedPage.path}</span>
              </div>
              <button
                type="button"
                onClick={() => setPreviewKey((k) => k + 1)}
                className="text-xs text-gray-500 hover:text-black underline underline-offset-2"
                data-testid="page-preview-refresh"
              >
                Refresh
              </button>
            </div>
            <iframe
              key={previewKey}
              src={selectedPage.path}
              title={`Preview of ${selectedPage.name || selectedPage.title}`}
              className="w-full bg-gray-50"
              style={{ height: 'calc(100vh - 200px)', minHeight: 600 }}
              data-testid="page-preview-iframe"
            />
            <div className="px-4 py-2 border-t border-gray-200 text-xs text-gray-500">
              Click <strong>Save Changes</strong> to update this preview.
            </div>
          </div>
        )}
      </div>

      {/* Shared media library picker — opened by any block editor via setPicker */}
      <MediaPickerModal
        open={!!picker}
        onClose={() => setPicker(null)}
        onSelect={(url) => {
          picker?.onSelect?.(url);
          setPicker(null);
        }}
      />
    </div>
  );
};

export default UniversalPageManager;
